import React, { useEffect, useRef, useState } from 'react';
import './MoveToGroupMenu.scss';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { useTranslation } from 'react-i18next';
import { ProjectGroup } from '../../../../shared/models/projects/ProjectGroup';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';

interface MoveToGroupMenuProps {
  groups: ProjectGroup[];
  currentGroupId: number | null;
  onSelect: (groupId: number | null) => void;
  buttonId: string;
}

const MoveToGroupMenu: React.FC<MoveToGroupMenuProps> = ({
  groups,
  currentGroupId,
  onSelect,
  buttonId,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<Menu>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const availableGroups = groups.filter((group) => group.id !== currentGroupId);

  useEffect(() => {
    const handleScroll = () => {
      if (menuRef.current) {
        menuRef.current.hide({} as React.SyntheticEvent);
      }
    };

    const layoutElement = document.querySelector('.layout__right');
    if (layoutElement && isMenuOpen) {
      layoutElement.addEventListener('scroll', handleScroll);
      return () => layoutElement.removeEventListener('scroll', handleScroll);
    }
  }, [isMenuOpen]);

  const handleSelect = (groupId: number | null) => {
    menuRef.current?.hide({} as React.SyntheticEvent);
    onSelect(groupId);
  };

  return (
    <>
      <Button
        id={buttonId}
        className="action-btn"
        onClick={(e) => menuRef.current?.toggle(e)}
      >
        <span className="ico--move ico--xs" />
      </Button>
      <CustomTooltip tooltipText="moveToGroup" targetRef={buttonId} />
      <Menu
        ref={menuRef}
        popup
        appendTo={document.body}
        className="move-to-group-menu__popup"
        onShow={() => setIsMenuOpen(true)}
        onHide={() => setIsMenuOpen(false)}
        model={[
          {
            template: (
              <div className="move-to-group-menu__list">
                {currentGroupId !== null && (
                  <>
                    <div
                      className="move-to-group-menu__item"
                      onClick={() => handleSelect(null)}
                    >
                      <span>{t('ungrouped')}</span>
                    </div>
                    {availableGroups.length > 0 && (
                      <div className="move-to-group-menu__separator" />
                    )}
                  </>
                )}
                {availableGroups.map((group) => (
                  <div
                    key={group.id}
                    className="move-to-group-menu__item"
                    onClick={() => handleSelect(group.id)}
                  >
                    <span
                      className="move-to-group-menu__dot"
                      style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                    />
                    <span>{group.name}</span>
                  </div>
                ))}
              </div>
            ),
          },
        ]}
      />
    </>
  );
};

export default MoveToGroupMenu;
